/* eslint-disable import/no-import-module-exports */
import React from 'react';
import ReactDOM from 'react-dom';
import { Auth, API, Storage, Logger } from 'aws-amplify';
import deepForceUpdate from 'react-deep-force-update';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import queryString from 'query-string';
import isMobile from 'is-mobile';
import { createPath } from 'history';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { ThemeProvider } from '@material-ui/core';
import { H } from 'highlight.run';
import flagsmith from 'flagsmith';
import App, { UnauthApp } from './components/App';
import { configureStore } from './store/configureStore';
import history from './history';
import { updateMeta } from './DOMUtils';
import router from './router';
import { GetCustomTheme, DefaultFlagsContext } from './context';
import { loadState } from './utils/StorageUtils';
import { UseUnAuthApp } from './utils/AuthUtils';

const initialState = loadState();

// check if session data is present and if so override userId
// in initialStore and local storage based on session
const { portalConfig, sessionData } = window.App;
const { currentPortalSession, portalIdToSession } = window.App.sessionData;
// we need to get the current portal session related email to set it as LastAuthUser
const { userId } = portalIdToSession[currentPortalSession] || {};
if (userId) {
  window.localStorage.setItem(
    `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
    userId,
  );
  if (initialState?.user) {
    initialState.user.id = userId;
  }
}

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => {
      removeCss.forEach(f => f());
    };
  },
  isMobile: isMobile(),
  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: configureStore(initialState, { history }),
  storeSubscription: null,
  stripeContext: window.App.stripeContext,
  portalConfig,
  marketingSiteData: window.App.marketingSiteData,
  flags: DefaultFlagsContext,
  sessionData,
};

const customTheme = GetCustomTheme(context.isMobile, context.portalConfig);

const authConfig = {
  ...context.portalConfig.AWS.Auth,
  authenticationFlowType: 'USER_PASSWORD_AUTH',
};
Auth.configure(authConfig);
API.configure(context.portalConfig.AWS.API);
Storage.configure(context.portalConfig.AWS.Storage);
Logger.LOG_LEVEL = window.App.logLevel;

const container = document.getElementById('app');
let currentLocation = history.location;
let appInstance;

const scrollPositionsHistory = {};

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  const isInitialRender = !action;
  try {
    context.pathname = context.marketingSiteData
      ? '/marketingsite'
      : location.pathname;
    context.query = queryString.parse(location.search);

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve(context);

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key && action !== 'FLAG_UPDATE') {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    if (window.gtag) {
      // when gtag is defined we need to call page_view
      window.gtag('event', 'page_view');
    }

    const renderReactApp = isInitialRender ? ReactDOM.hydrate : ReactDOM.render;
    let AppComponent = App;
    if (
      portalConfig.viewMode === 'internal' &&
      UseUnAuthApp(context.pathname, context.query)
    ) {
      // copilot brandded application for login/signup/
      AppComponent = UnauthApp;
      if (context.pathname === '/admin') {
        // when rendering admin page we use the UnauthApp and set the auth flow to be CUSTOM_AUTH
        authConfig.authenticationFlowType = 'CUSTOM_AUTH';
        Auth.configure(authConfig);
      }
    }

    // When mobile client is detected and not loading an unauth path
    // render the mobile page.
    appInstance = renderReactApp(
      <ThemeProvider theme={customTheme}>
        <AppComponent context={context}>{route.component}</AppComponent>
      </ThemeProvider>,
      container,
      () => {
        if (isInitialRender) {
          // Switch off the native scroll restoration behavior and handle it manually
          // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
          if (window.history && 'scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
          }

          const elem = document.getElementById('css');
          if (elem) elem.parentNode.removeChild(elem);

          const materialStyles = document.querySelector('#material-css');
          if (materialStyles) {
            materialStyles.parentElement.removeChild(materialStyles);
          }

          return;
        }

        updateMeta('description', route.description);
        // Update necessary tags in <head> at runtime here, ie:
        // updateMeta('keywords', route.keywords);
        // updateCustomMeta('og:url', route.canonicalUrl);
        // updateCustomMeta('og:image', route.imageUrl);
        // updateLink('canonical', route.canonicalUrl);
        // etc.

        let scrollX = 0;
        let scrollY = 0;
        const pos = scrollPositionsHistory[location.key];
        if (pos) {
          scrollX = pos.scrollX;
          scrollY = pos.scrollY;
        } else {
          const targetHash = location.hash.substr(1);
          if (targetHash) {
            const target = document.getElementById(targetHash);
            if (target) {
              scrollY = window.pageYOffset + target.getBoundingClientRect().top;
            }
          }
        }

        // Restore the scroll position if it was saved into the state
        // or scroll to the given #hash anchor
        // or scroll to top of the page
        window.scrollTo(scrollX, scrollY);

        // Google Analytics tracking. Don't send 'pageview' event after
        // the initial rendering, as it was already sent
        if (window.ga) {
          window.ga('send', 'pageview', createPath(location));
        }
      },
    );
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      console.error('RSK will reload your page after error');
      window.location.reload();
    }
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange);
onLocationChange(currentLocation);

// In lieu of having portalConfig in our localStorage or Cookie,
// the back button loses the current portalHeader from the config,
// and we won't know which portal you belong to anymore.
if (window.history && window.history.pushState) {
  window.onpopstate = evt => {
    if (
      portalConfig.portalHeader === 'workspace' &&
      !window.location.pathname.startsWith('/onboarding')
    ) {
      window.location.reload();
    }
  };
}

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange(currentLocation);
  });
}

LicenseManager.setLicenseKey(
  'CompanyName=Keep Donating,LicensedApplication=Donations,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=1,AssetReference=AG-012649,ExpiryDate=13_January_2022_[v2]_MTY0MjAzMjAwMDAwMA==1006e1b408e42f2543ca1c422172624e',
);

if (window.App.highlightOrgId) {
  H.init(window.App.highlightOrgId, {
    enableSegmentIntegration: true,
    enableStrictPrivacy: true,
    networkRecording: {
      recordHeadersAndBody: true,
      urlBlocklist: [
        'https://cognito-idp.us-west-2.amazonaws.com',
        'https://cognito-identity.us-west-2.amazonaws.com',
        'https://app-api.copilot.com/onboarding/login',
        'https://app-api.copilot.com/onboarding',
      ],
    },
    version: window.App.appVersion,
  });
}

if (window.App.sentryDSN) {
  Sentry.init({
    dsn: window.App.sentryDSN,
    integrations: [new Sentry.Replay(), new BrowserTracing()],
    release: window.App.appVersion,
    environment: 'production',

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.2,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
  });
}

if (window.App.flagEnvId) {
  flagsmith.init({
    environmentID: window.App.flagEnvId,
    onChange: () => {
      // Occurs whenever flags are changed
      // Determines if the update came from the server or local cached storage
      // const { isFromServer } = params;

      // Check for a feature, please add in alphabetical order
      context.flags.EnableAdvancedBranding = flagsmith.hasFeature(
        'enable_advanced_branding',
      );

      context.flags.EnableChannelsClientDetails = flagsmith.hasFeature(
        'enable_channels_client_details',
      );
      context.flags.EnableClientPersonalBranding = flagsmith.hasFeature(
        'enable_client_personal_branding',
      );

      context.flags.EnableClientsImport = flagsmith.hasFeature(
        'enable_clients_import',
      );
      context.flags.EnableClientInviteEnhancement = flagsmith.hasFeature(
        'enable_client_invite_enhancement',
      );

      context.flags.EnableEmailNotificationsDigest = flagsmith.hasFeature(
        'enable_email_notifications_digest',
      );

      context.flags.EnableFeeFlexibilityFlow = true;

      context.flags.EnableFilesNotificationCenter = flagsmith.hasFeature(
        'enable_files_notification_center',
      );

      context.flags.EnableImprovedForms = flagsmith.hasFeature(
        'enable_forms_v2',
      );

      context.flags.EnableImprovedReferralsProgram = true;

      context.flags.EnableNewPricingPlans = true;

      context.flags.EnableNotificationSettings = flagsmith.hasFeature(
        'enable_notifications_settings',
      );
      context.flags.EnableUpdatedSignatureComponents = flagsmith.hasFeature(
        'enable_updated_signature_components',
      );

      context.flags.EnableUpdatedSignatureCreationFlow = flagsmith.hasFeature(
        'enable_updated_signature_creation_flow',
      );

      context.flags.EnableFilesApi = flagsmith.hasFeature('enable_files_api');

      context.flags.EnableMfa = flagsmith.hasFeature('enable_mfa');

      onLocationChange(currentLocation, 'FLAG_UPDATE');
    },
  });
}
